import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import Layout from "@/components/Layout";
import { useDashboardData } from "@/components/dashboard/useDashboardData";
import DashboardHeader from "@/components/dashboard/DashboardHeader";
import DashboardOverview from "@/components/dashboard/DashboardOverview";
import DashboardInsights from "@/components/dashboard/DashboardInsights";
import DashboardTeam from "@/components/dashboard/DashboardTeam";
import Onboarding from "@/components/Onboarding";
import FirstTimeUserDemo from "@/components/onboarding/FirstTimeUserDemo";
import IntroToStep from "@/components/onboarding/IntroToStep";
import OnboardingTour from "@/components/onboarding/OnboardingTour";
import PostSignupGuide from "@/components/onboarding/PostSignupGuide";
import { TeamType } from "@/types/user";
import { redeemTeamInvitation } from "@/components/team/services/teamInviteService";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";

interface InvitationData {
  inviteCode: string;
  teamId: string;
  teamName: string;
  userId?: string;
  timestamp?: number;
  organizationId?: string | null;
  organizationName?: string | null;
}

export default function Index() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    teamMembers,
    weeklyInsightData,
    actionSuggestions,
    activeTab,
    setActiveTab,
  } = useDashboardData();
  const { user, loading, updateUserProfile, refreshUser } = useUser();
  const { hasTeamFeatures } = useSubscription();
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData | null>(null);
  const [showIntro, setShowIntro] = useState(false);
  const [selectedPlanFromStorage, setSelectedPlanFromStorage] = useState<string | null>(null);
  const [showPostSignupGuide, setShowPostSignupGuide] = useState(false);


  // Check for invitation data and selected plan from pricing page
  useEffect(() => {
    // Check if returning from payment with onboarding in progress
    const continueOnboarding = searchParams.get('continueOnboarding') === 'true';
    const onboardingInProgress = sessionStorage.getItem('onboarding-in-progress') === 'true';
    
    if (continueOnboarding && onboardingInProgress) {
      // Mark that plan selection was completed
      sessionStorage.setItem('onboarding-plan-completed', 'true');
    }
    
    const storedInvitation = localStorage.getItem('invitationData') || sessionStorage.getItem('invitationData');
    if (storedInvitation) {
      try {
        const parsed = JSON.parse(storedInvitation);
        
        // Check if invitation data is recent (within 24 hours)
        const isRecent = parsed.timestamp && (Date.now() - parsed.timestamp) < 24 * 60 * 60 * 1000;
        
        if (isRecent) {
          if (process.env.NODE_ENV === 'development') {
            console.log("=== INVITATION DATA FOUND ===");
            console.log("Invitation data:", parsed);
          }
          setInvitationData(parsed);
        } else {
          // Clean up old invitation data
          localStorage.removeItem('invitationData');
          sessionStorage.removeItem('invitationData');
        }
      } catch (error) {
        console.error('Error parsing invitation data:', error);
        localStorage.removeItem('invitationData');
        sessionStorage.removeItem('invitationData');
      }
    }

    // Check for selected plan from pricing page
    const storedPlan = localStorage.getItem('selectedPlan');
    if (storedPlan) {
      setSelectedPlanFromStorage(storedPlan);
    }
  }, []);

  // Keep invitation data in sync with onboarding invite validation
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleInvitationDataUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<InvitationData>;
      if (customEvent.detail) {
        setInvitationData(customEvent.detail);
      }
    };

    window.addEventListener("invitationDataUpdated", handleInvitationDataUpdated);

    return () => {
      window.removeEventListener("invitationDataUpdated", handleInvitationDataUpdated);
    };
  }, []);

  const handleNewCheckIn = () => {
    console.log("=== STARTING CHECK-IN ===");
    console.log("User authenticated: true"); // Auth is handled at App.tsx level
    console.log("User data:", user);
    console.log("Navigating to /check-in");
    navigate("/check-in");
  };

  // Debug current user state
  if (user) {
    console.log("=== CURRENT USER STATE ===");
    console.log("Persona:", user.persona);
    console.log("Role:", user.role);
    console.log("TeamId:", user.teamId);
    console.log("OrganizationId:", user.organizationId);
    console.log("isSoloUser flag:", user.isSoloUser);
    console.log("isIndependentSolo flag:", user.isIndependentSolo);
  }

  const showTeamOverview =
    user?.role === "manager" ||
    user?.teamType === "engineering" ||
    user?.teamType === "design";

  // Check if user needs to see intro
  useEffect(() => {
    // Show intro for ALL new users, regardless of invitation status
    if (user?.isNewUser && user?.id && !localStorage.getItem(`hasSeenIntro_${user.id}`)) {
      setShowIntro(true);
    }
  }, [user?.isNewUser, user?.id]);

  // Handle completion of the intro
  const handleIntroComplete = () => {
    if (user?.id) {
      localStorage.setItem(`hasSeenIntro_${user.id}`, 'true');
    }
    setShowIntro(false);
  };

  // Handle completion of the onboarding process
  const handleOnboardingComplete = async () => {
    setOnboardingComplete(true);
    
    // Ensure user metadata is marked as onboarding completed
    try {
      await updateUserProfile({ isNewUser: false });
      // Force refresh user profile to get updated isNewUser flag from backend
      // This prevents redirect to onboarding when navigating to other pages
      await refreshUser();
    } catch (error) {
      console.error("Error updating onboarding completion status:", error);
    }
    
    // Show post-signup guide for new users
    if (user?.id && !localStorage.getItem(`postSignupGuide_dismissed_${user.id}`)) {
      setShowPostSignupGuide(true);
    }
    
    // Clean up invitation data and stored plan after onboarding
    if (invitationData) {
      localStorage.removeItem('invitationData');
      sessionStorage.removeItem('invitationData');
      setInvitationData(null);
    }
    
    // Clean up stored plan selection
    localStorage.removeItem('selectedPlan');
    localStorage.removeItem('voucherCode');
    setSelectedPlanFromStorage(null);
  };

  // Check if we should show post-signup guide for users who completed onboarding
  useEffect(() => {
    if (!user?.isNewUser && onboardingComplete && user?.id) {
      const hasSeenGuide = localStorage.getItem(`postSignupGuide_dismissed_${user.id}`);
      if (!hasSeenGuide) {
        setShowPostSignupGuide(true);
      }
    }
  }, [user?.isNewUser, onboardingComplete, user?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Note: Authentication is handled at App.tsx level (like web_old)
  // Index only renders when user is authenticated

  return (
    <>
        {/* Show intro for new users first */}
        {showIntro ? (
          <IntroToStep onComplete={handleIntroComplete} />
        ) : (
          /* Afficher l'onboarding spécial pour les utilisateurs invités */
          invitationData && user?.isNewUser && !onboardingComplete ? (
            <div className="min-h-screen bg-background">
              <Onboarding 
                onComplete={handleOnboardingComplete}
                isInvitedUser={true}
                invitedTeamId={invitationData.teamId}
                invitedTeamName={invitationData.teamName}
                invitedTeamType={"general" as TeamType} // Par défaut, on peut l'améliorer plus tard
              />
            </div>
          ) : (
            /* Afficher l'onboarding normal pour les nouveaux utilisateurs non invités */
            user?.isNewUser && !onboardingComplete ? (
              <div className="min-h-screen bg-background">
                <Onboarding onComplete={handleOnboardingComplete} />
              </div>
          ) : invitationData && !user?.isNewUser ? (
            /* Handle case where existing user has pending invitation */
            <div className="min-h-screen bg-background">
              <div className="flex items-center justify-center min-h-screen p-4">
                <div className="w-full max-w-md mx-auto">
                  <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 text-center">
                    <h2 className="text-xl font-semibold mb-4">Join Team Invitation</h2>
                    <p className="text-gray-600 mb-4">
                      You have a pending invitation to join <strong>{invitationData.teamName}</strong>.
                    </p>
                    <div className="space-y-3">
                      <button 
                        onClick={async () => {
                          try {
                            const result = await redeemTeamInvitation(
                              invitationData.inviteCode,
                              user?.id
                            );
                            
                            if (result.success) {
                              toast.success(`Successfully joined ${result.teamName}!`);
                              handleOnboardingComplete();
                            } else {
                              toast.error(result.error || "Failed to join team");
                            }
                          } catch (error) {
                            console.error('Error joining team:', error);
                            toast.error("Failed to join team. Please try again.");
                          }
                        }}
                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg text-base font-medium hover:bg-blue-700 transition-colors"
                      >
                        Join Team
                      </button>
                      <button 
                        onClick={handleOnboardingComplete}
                        className="w-full bg-gray-300 text-gray-700 py-3 px-4 rounded-lg text-base font-medium hover:bg-gray-400 transition-colors"
                      >
                        Skip for Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <Layout>
              <div className="mb-8">
                <DashboardHeader
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  userName={user?.name || "User"}
                />
              </div>

              {activeTab === "overview" && (
                <DashboardOverview
                  teamMembers={teamMembers}
                  actionSuggestions={actionSuggestions}
                  showTeamOverview={showTeamOverview}
                  setActiveTab={setActiveTab}
                  handleNewCheckIn={handleNewCheckIn}
                />
              )}

              {activeTab === "insights" && <DashboardInsights />}

              {activeTab === "team" && <DashboardTeam />}

              {/* FirstTimeUserDemo will only show for users who completed onboarding 
                  but haven't seen the demo yet (hasSeenDemo is explicitly false) */}
              <FirstTimeUserDemo />
              
              {/* Post-signup guide for users who just completed onboarding */}
              {showPostSignupGuide && (
                <PostSignupGuide 
                  onComplete={() => {
                    setShowPostSignupGuide(false);
                    if (user?.id) {
                      localStorage.setItem(`postSignupGuide_completed_${user.id}`, 'true');
                    }
                  }}
                  onDismiss={() => {
                    setShowPostSignupGuide(false);
                    if (user?.id) {
                      localStorage.setItem(`postSignupGuide_dismissed_${user.id}`, 'true');
                    }
                  }}
                />
              )}
              
              {/* Onboarding tour for new users */}
              <OnboardingTour />
            </Layout>
            )
          )
        )}
    </>
  );
}
